import { FormInput } from "@components/FormInput/FormInput";
import { FORM_ACTIONS } from "@models/FormActions";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { onErrorShow } from "@utils/onErrorShow";
import { useEffect, useReducer, useRef, useState } from "react";
import { onInfoShow } from "@utils/onInfoShow";
import { useAuth } from "@Auth/useAuth";
import { useDrawer } from "@hooks/useDrawer";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { useProduct } from "@features/products/queries/useProducts";
import {
  useCreateProduct,
  useUpdateProduct,
} from "@features/products/queries/useProductMutations";
import { Section } from "@common/SimpleDrawer/components/Section";
import { Footer } from "@common/SimpleDrawer/components/Footer";
import { FormAutocomplete } from "@components/FormAutocomplete/FormAutoComplete";
import { useTagsStore } from "@stores/tagsStore";
import { Chip, Typography } from "@mui/material";
import { Box } from "@mui/material";
import { addTransparency } from "@utils/colorUtils";
import { onSuccessShow } from "@utils/onSuccessShow";
import {
  CustomFieldsClient,
  ICustomFieldDefinition,
  ICustomFieldValue,
} from "@Clients/CustomFieldsClient";
import { Dropdown } from "@components/Dropdown/Dropdown";

type ProductInitialState = {
  name: string;
  description: string;
  price: string | null;
  sku: string;
  tags: string[];
  customFields: Record<string, string | number | boolean | null | undefined>;
  errors: Record<string, string>;
};

// Initial State
const initialState: ProductInitialState = {
  name: "",
  description: "",
  price: null,
  sku: "",
  customFields: {},
  errors: {},
  tags: [],
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: T[keyof T];
  errors?: Record<string, string>;
}

// Reducer Function
function formReducer(
  state: ProductInitialState,
  action: Action<ProductInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD: {
      if (!action.field) return state;
      return {
        ...state,
        [action.field!]: action.value,
        errors: {
          ...state.errors,
          [action.field!]: "",
        },
      };
    }
    case FORM_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: action.errors || {},
      };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

interface ProductFormProps {
  handleClose: () => void;
  createFromStockView?: boolean;
}

export const ProductForm = ({
  handleClose,
  createFromStockView,
}: ProductFormProps) => {
  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<ProductInitialState>,
      Action<ProductInitialState>
    >,
    initialState
  );

  const { setTouch } = useConfirmationModalStore();
  const { baseUser } = useAuth();

  const {
    close: closeOnSubmit,
    context,
    selectedId,
  } = useDrawer();

  // Source the prefill from the specific product, not the products list. On a
  // product detail page this is already in the React Query cache, so it's
  // available synchronously when the drawer opens — no race with a list fetch.
  const { data: editingProduct } = useProduct(
    context === DRAWER_CONTEXT.EDIT_PRODUCT ? selectedId ?? undefined : undefined
  );
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const { tagValues, addProductTag, getProductTags, removeProductTag } =
    useTagsStore();
  const [customColumns, setCustomColumns] = useState<ICustomFieldDefinition[]>(
    []
  );
  const currentProductTagsRef = useRef<string[]>([]);
  const prefilledIdRef = useRef<string | null>(null);

  const getCustomFieldValue = (value: ICustomFieldValue) => {
    if (value.value_text !== null && value.value_text !== undefined)
      return value.value_text;
    if (value.value_number !== null && value.value_number !== undefined)
      return value.value_number;
    if (value.value_bool !== null && value.value_bool !== undefined)
      return value.value_bool;
    if (value.value_date !== null && value.value_date !== undefined)
      return value.value_date;
    if (value.value_json !== null && value.value_json !== undefined)
      return value.value_json;
    return null;
  };

  useEffect(() => {
    const fetchCustomColumns = async () => {
      if (baseUser?.organization_id) {
        try {
          const columns = await CustomFieldsClient.getDefinitionsByOrganizationId(
            baseUser.organization_id,
            "product"
          );
          setCustomColumns(columns);
        } catch (error) {
          console.error("Error fetching product custom fields:", error);
        }
      }
    };
    fetchCustomColumns();
  }, [baseUser?.organization_id]);

  // Prefill the form from the product being edited. This runs once per
  // product: the source data (the cached detail query and the tags fetch) can
  // settle or refetch while the drawer is open, and re-dispatching would
  // clobber edits the user has already typed. The ref resets whenever the
  // drawer leaves the edit context, so reopening re-prefills cleanly.
  useEffect(() => {
    if (context !== DRAWER_CONTEXT.EDIT_PRODUCT || !selectedId) {
      prefilledIdRef.current = null;
      return;
    }
    if (prefilledIdRef.current === selectedId || !editingProduct) return;
    prefilledIdRef.current = selectedId;

    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "name",
      value: editingProduct.name,
    });
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "sku",
      value: editingProduct.sku,
    });
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "description",
      value: editingProduct.description,
    });
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "price",
      value: editingProduct.price?.toString() || "",
    });

    getProductTags(selectedId).then((productTags) => {
      currentProductTagsRef.current = productTags.map(
        (tag) => tag.tag_values.id
      );
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "tags",
        value: productTags.map((tag) => tag.tag_values.id),
      });
    });
  }, [context, selectedId, editingProduct, getProductTags]);

  useEffect(() => {
    const fetchCustomFieldValues = async () => {
      if (
        context !== DRAWER_CONTEXT.EDIT_PRODUCT ||
        !selectedId ||
        !baseUser?.organization_id ||
        customColumns.length === 0
      ) {
        return;
      }
      try {
        const values = await CustomFieldsClient.getValuesByEntity(
          baseUser.organization_id,
          "product",
          selectedId
        );
        const definitionMap = new Map(
          customColumns.map((column) => [column.id, column.name])
        );
        const mappedValues: Record<
          string,
          string | number | boolean | null | undefined
        > = {};
        values.forEach((value) => {
          const fieldName = definitionMap.get(value.field_id);
          if (!fieldName) return;
          mappedValues[fieldName] = getCustomFieldValue(value) as
            | string
            | number
            | boolean
            | null
            | undefined;
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "customFields",
          value: mappedValues,
        });
      } catch (error) {
        console.error("Error fetching product custom field values:", error);
      }
    };
    fetchCustomFieldValues();
  }, [
    baseUser?.organization_id,
    context,
    customColumns,
    selectedId,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: name as keyof ProductInitialState,
      value,
    });
    if (!createFromStockView) setTouch(true);
  };

  const handleFormVerification = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const errors = {} as Record<string, string>;

    const { price } = state;
    // Only require name; SKU will default to name if left empty
    const requiredFields: (keyof ProductInitialState)[] = ["name"];

    requiredFields.forEach((field) => {
      if (!state[field]) errors[field] = `${field} is required`;
    });

    if (price && Number(price) <= 0)
      errors.price = "Price must be greater than zero";

    for (const column of customColumns) {
      if (column.required) {
        const fieldValue = state.customFields?.[column.name];
        if (
          fieldValue === null ||
          fieldValue === undefined ||
          (typeof fieldValue === "string" && !fieldValue.trim())
        ) {
          errors[`custom_${column.name}`] = `${column.label} is required`;
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      dispatch({ type: FORM_ACTIONS.SET_ERRORS, errors });
      onErrorShow("Please fix the errors before submitting");
      return;
    }
    return true;
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      const validForm = handleFormVerification(e);

      if (!validForm) return;

      const { name, sku, description, price, customFields } = state;
      const effectiveSku = (sku || "").trim() || (name || "").trim();
      const buildCustomFieldPayload = (entityId: string) => {
        const values = customColumns
          .map((column) => {
            const rawValue = customFields?.[column.name];
            const isEmptyValue =
              rawValue === null ||
              rawValue === undefined ||
              (typeof rawValue === "string" && rawValue.trim() === "");
            if (isEmptyValue) return null;

            const baseValue = {
              organization_id: baseUser!.organization_id!,
              entity_type: "product" as const,
              entity_id: entityId,
              field_id: column.id,
              value_text: null,
              value_number: null,
              value_bool: null,
              value_date: null,
              value_json: null,
            };

            switch (column.type) {
              case "text":
              case "textarea":
              case "select":
                return { ...baseValue, value_text: String(rawValue) };
              case "number":
                return {
                  ...baseValue,
                  value_number:
                    rawValue === "" ? null : Number(rawValue as number),
                };
              case "boolean":
                return { ...baseValue, value_bool: Boolean(rawValue) };
              case "date":
                return { ...baseValue, value_date: String(rawValue) };
              default:
                return { ...baseValue, value_text: String(rawValue) };
            }
          })
          .filter(Boolean) as Array<
            Omit<ICustomFieldValue, "id" | "created_at" | "updated_at">
          >;

        return values;
      };

      if (context === DRAWER_CONTEXT.EDIT_PRODUCT && selectedId) {
        // **Update existing product**
        await updateProductMutation.mutateAsync({
          id: selectedId,
          name: name!,
          description: description || "",
          price: price ? Number(price) : null,
          sku: effectiveSku,
          organization_id: baseUser!.organization_id,
        });
        await CustomFieldsClient.deleteValuesForEntity(
          baseUser!.organization_id,
          "product",
          selectedId
        );
        const values = buildCustomFieldPayload(selectedId);
        if (values.length > 0) {
          await CustomFieldsClient.upsertValues(values);
        }
        const newTags = state.tags?.filter(
          (tag) => !currentProductTagsRef.current.includes(tag)
        );

        if (newTags && newTags.length > 0) {
          newTags.forEach(async (tag) => {
            await addProductTag(selectedId, tag);
          });
        }

        onInfoShow("Product updated successfully");
      } else {
        // **Create a new product**
        const newProduct = await createProductMutation.mutateAsync({
          name: name!,
          description: description || "",
          price: price ? Number(price) : null,
          sku: effectiveSku,
          organization_id: baseUser?.organization_id || "",
        });
        const values = buildCustomFieldPayload(newProduct.id);
        if (values.length > 0) {
          await CustomFieldsClient.upsertValues(values);
        }
        if (state.tags) {
          state.tags.forEach(async (tag) => {
            await addProductTag(newProduct.id, tag);
          });
        }
        onInfoShow("Product created successfully");
      }

      closeOnSubmit();
      dispatch({ type: FORM_ACTIONS.RESET });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateFromStockWindow = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const validForm = handleFormVerification(e);

    if (!validForm) return;

    const { name, sku, description, price, customFields } = state;
    const buildCustomFieldPayload = (entityId: string) => {
      const values = customColumns
        .map((column) => {
          const rawValue = customFields?.[column.name];
          const isEmptyValue =
            rawValue === null ||
            rawValue === undefined ||
            (typeof rawValue === "string" && rawValue.trim() === "");
          if (isEmptyValue) return null;

          const baseValue = {
            organization_id: baseUser!.organization_id!,
            entity_type: "product" as const,
            entity_id: entityId,
            field_id: column.id,
            value_text: null,
            value_number: null,
            value_bool: null,
            value_date: null,
            value_json: null,
          };

          switch (column.type) {
            case "text":
            case "textarea":
            case "select":
              return { ...baseValue, value_text: String(rawValue) };
            case "number":
              return {
                ...baseValue,
                value_number:
                  rawValue === "" ? null : Number(rawValue as number),
              };
            case "boolean":
              return { ...baseValue, value_bool: Boolean(rawValue) };
            case "date":
              return { ...baseValue, value_date: String(rawValue) };
            default:
              return { ...baseValue, value_text: String(rawValue) };
          }
        })
        .filter(Boolean) as Array<
          Omit<ICustomFieldValue, "id" | "created_at" | "updated_at">
        >;
      return values;
    };

    const newProduct = await createProductMutation.mutateAsync({
      name: name!,
      description: description || "",
      price: price ? Number(price) : null,
      sku: sku!,
      organization_id: baseUser?.organization_id || "",
    });
    const values = buildCustomFieldPayload(newProduct.id);
    if (values.length > 0) {
      await CustomFieldsClient.upsertValues(values);
    }
    onInfoShow("Product created successfully");
    handleClose();
    setTouch(false);
  };

  const handleCustomFieldChange = (
    fieldName: string,
    value: string | number | boolean | null | undefined
  ) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "customFields",
      value: {
        ...(state.customFields || {}),
        [fieldName]: value,
      },
    });
    if (!createFromStockView) setTouch(true);
  };

  const handleTagChange = (value: string) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "tags",
      value: [...(state.tags || []), value],
    });
  };
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormInput
            name="name"
            label="Product Name"
            onChange={handleChange}
            value={state.name}
            errorMessage={state.errors?.name}
          />
          <FormInput
            name="sku"
            label="Product SKU"
            onChange={handleChange}
            value={state.sku}
            errorMessage={state.errors?.sku}
          />
          <FormInput
            name="description"
            label="Product Description"
            onChange={handleChange}
            value={state.description}
            errorMessage={state.errors?.description}
          />
          <FormInput
            name="price"
            label="Product Price"
            onChange={handleChange}
            value={state.price || ""}
            errorMessage={state.errors?.price}
          />

          {customColumns
            .sort((a, b) => a.display_order - b.display_order)
            .map((column) => {
              const fieldValue =
                state.customFields?.[column.name] || column.default_value || "";

              if (column.type === "text") {
                return (
                  <FormInput
                    key={column.id}
                    name={`custom_${column.name}`}
                    label={column.label}
                    onChange={(e) =>
                      handleCustomFieldChange(column.name, e.target.value)
                    }
                    value={fieldValue as string}
                    placeholder={column.placeholder || ""}
                    errorMessage={
                      column.required && !fieldValue
                        ? `${column.label} is required`
                        : state.errors?.[`custom_${column.name}`]
                    }
                  />
                );
              }

              if (column.type === "textarea") {
                return (
                  <Box key={column.id} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {column.label}
                      {column.required && (
                        <Typography component="span" color="error">
                          {" *"}
                        </Typography>
                      )}
                    </Typography>
                    <textarea
                      name={`custom_${column.name}`}
                      value={fieldValue as string}
                      onChange={(e) =>
                        handleCustomFieldChange(column.name, e.target.value)
                      }
                      placeholder={column.placeholder || ""}
                      rows={4}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: `1px solid ${state.errors?.[`custom_${column.name}`]
                            ? "#f44336"
                            : "#e0e0e0"
                          }`,
                        fontSize: "14px",
                        fontFamily: "inherit",
                        resize: "vertical",
                        minHeight: "80px",
                      }}
                    />
                    {column.help_text && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {column.help_text}
                      </Typography>
                    )}
                    {state.errors?.[`custom_${column.name}`] && (
                      <Typography variant="caption" color="error">
                        {state.errors[`custom_${column.name}`]}
                      </Typography>
                    )}
                  </Box>
                );
              }

              if (column.type === "number") {
                return (
                  <FormInput
                    key={column.id}
                    name={`custom_${column.name}`}
                    label={column.label}
                    type="number"
                    onChange={(e) =>
                      handleCustomFieldChange(
                        column.name,
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                    value={fieldValue as number | string}
                    placeholder={column.placeholder || ""}
                    errorMessage={
                      column.required && (fieldValue === "" || fieldValue === null)
                        ? `${column.label} is required`
                        : state.errors?.[`custom_${column.name}`]
                    }
                  />
                );
              }

              if (column.type === "date") {
                return (
                  <FormInput
                    key={column.id}
                    name={`custom_${column.name}`}
                    label={column.label}
                    type="date"
                    onChange={(e) =>
                      handleCustomFieldChange(column.name, e.target.value)
                    }
                    value={fieldValue as string}
                    errorMessage={
                      column.required && !fieldValue
                        ? `${column.label} is required`
                        : state.errors?.[`custom_${column.name}`]
                    }
                  />
                );
              }

              if (column.type === "boolean") {
                return (
                  <Box
                    key={column.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(fieldValue)}
                      onChange={(e) =>
                        handleCustomFieldChange(column.name, e.target.checked)
                      }
                      style={{ marginRight: 8 }}
                    />
                    <Typography variant="body2">
                      {column.label}
                      {column.required && (
                        <Typography component="span" color="error">
                          {" *"}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                );
              }

              if (column.type === "select") {
                const selectOptions = (column.field_options || []).map(
                  (option) => ({
                    value: option.value,
                    label: option.label,
                  })
                );
                return (
                  <Box key={column.id} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {column.label}
                      {column.required && (
                        <Typography component="span" color="error">
                          {" *"}
                        </Typography>
                      )}
                    </Typography>
                    <Dropdown
                      options={selectOptions}
                      value={fieldValue as string}
                      onChange={(value) =>
                        handleCustomFieldChange(column.name, value)
                      }
                      placeholder={
                        column.placeholder || `Select ${column.label}`
                      }
                      variant="outlined"
                      size="small"
                    />
                    {column.help_text && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {column.help_text}
                      </Typography>
                    )}
                  </Box>
                );
              }

              return null;
            })}
        </Section>
        <Section>
          <Typography variant="h5">Product Tags</Typography>
          <FormAutocomplete
            dropdownPlaceholder="Select a tag"
            label="Tags"
            onChange={handleTagChange}
            // value={state.tags}
            errorMessage={state.errors?.tags}
            options={tagValues.map((tag) => ({
              label: tag.value,
              value: tag.id,
            }))}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {tagValues
              .filter((tag) => state.tags?.includes(tag.id))
              .map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.value}
                  sx={{
                    backgroundColor: addTransparency(
                      tag.metadata.color as string,
                      0.1
                    ),
                    height: "28px",
                    fontSize: "14px",
                    width: "fit-content",
                    padding: " 2px 8px",
                    border: `1px solid ${tag.metadata.color}`,
                    color: tag.metadata.color as string,
                  }}
                  onDelete={async () => {
                    if (
                      currentProductTagsRef.current.filter((t) => t === tag.id)
                    ) {
                      await removeProductTag(selectedId!, tag.id);
                      getProductTags(selectedId!).then((productTags) => {
                        currentProductTagsRef.current = productTags.map(
                          (tag) => tag.tag_values.id
                        );
                        onSuccessShow("Tag removed successfully");
                      });
                    }

                    dispatch({
                      type: FORM_ACTIONS.SET_FIELD,
                      field: "tags",
                      value: state.tags?.filter((t) => t !== tag.id),
                    });
                  }}
                />
              ))}
          </Box>
        </Section>
      </Box>

      <Footer
        handleClose={handleClose}
        handleSubmit={async (e) => {
          createFromStockView
            ? await handleCreateFromStockWindow(e)
            : handleSubmit(e);
        }}
      />
    </Box>
  );
};
